import React, { useState } from 'react';
import { useTranslate } from '@bynder/localization';
import { Button, Dropdown, Form, Modal } from '@bynder/design-system';
import modalContainer from 'packages/common/modalContainer';
import { useDeferredClose } from '~/hooks/useDeferredClose';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    variationName: string;
    onTranscribe: (language: string) => void;
};

type Language = {
    code: string;
    name: string;
};

const TRANSCRIBE_LANGUAGES: Language[] = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'it', name: 'Italian' },
];

const VariationTranscribeModal = ({ isOpen, onClose, variationName, onTranscribe }: Props) => {
    const { translate } = useTranslate();
    const [selectedLanguage, setSelectedLanguage] = useState<Language | null>(null);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [shouldRender, onCloseAnimationEnd] = useDeferredClose(isOpen);

    const handleTranscribe = () => {
        if (!selectedLanguage) {
            return;
        }

        setIsTranscribing(true);
        onTranscribe(selectedLanguage.code);
        setIsTranscribing(false);
        onClose();
    };

    const handleClose = () => {
        setSelectedLanguage(null);
        onClose();
    };

    if (!shouldRender) {
        return null;
    }

    return (
        <Modal
            container={modalContainer}
            isOpen={isOpen}
            onClose={handleClose}
            onCloseAnimationEnd={onCloseAnimationEnd}
            title={translate('design.sidebar.variations.modal.transcribe.title', { name: variationName })}
            actionPrimary={
                <Button variant="primary" onClick={handleTranscribe} isDisabled={!selectedLanguage || isTranscribing}>
                    {translate('design.sidebar.variations.modal.transcribe.button.transcribe')}
                </Button>
            }
            actionSecondary={
                <Button variant="secondary" onClick={handleClose}>
                    {translate('design.sidebar.variations.modal.transcribe.button.cancel')}
                </Button>
            }
        >
            <Form>
                <Form.Group>
                    <Form.Label htmlFor="transcribe_language">
                        {translate('design.sidebar.variations.modal.transcribe.language.translate_to')}
                    </Form.Label>
                    <Dropdown
                        trigger={({ isOpen: isDropdownOpen, ...triggerProps }) => (
                            <Button
                                isFullWidth
                                isPressed={isDropdownOpen}
                                rightIcon={<Dropdown.Arrow />}
                                {...triggerProps}
                            >
                                <Dropdown.SelectButtonText>
                                    {selectedLanguage?.name ||
                                        translate('design.sidebar.variations.modal.transcribe.language.placeholder')}
                                </Dropdown.SelectButtonText>
                            </Button>
                        )}
                    >
                        {TRANSCRIBE_LANGUAGES.map((language) => (
                            <Dropdown.Item
                                key={language.code}
                                isChecked={selectedLanguage?.code === language.code}
                                onClick={() => setSelectedLanguage(language)}
                            >
                                {language.name}
                            </Dropdown.Item>
                        ))}
                    </Dropdown>
                </Form.Group>
            </Form>
        </Modal>
    );
};

export default VariationTranscribeModal;
